import { useCallback, useMemo } from 'react';
import { config } from '../../config/config';
import { ValueOf } from '../../config/constants';
import { generateChartData, generateChartDataParams } from '../../helpers';
import moment from 'moment';
import { parseEther } from 'ethers/lib/utils';

let cachedTVL: Record<string, { time: string; value: number }[]> = {};

const tvlStartValues: Record<string, number> = {
    blueChip: 5_000_000,
    altCoin: 2_000_000,
    degen: 1_000_000,
};

const generateMockTVLData = (
    timeframe: ValueOf<typeof config.areaChartTimeframes>,
    liquidityPoolId: string,
    baseValue: number
) => {
    const cacheKey = `${liquidityPoolId}_${timeframe}`;

    if (cachedTVL[cacheKey]) return cachedTVL[cacheKey];

    const chartDataParams = generateChartDataParams(timeframe);
    const chartData = generateChartData(
        chartDataParams.startTime,
        parseEther('0'),
        chartDataParams.amountToGenerate,
        chartDataParams.timeInterval
    );

    let currentValue = baseValue;
    const data = chartData.map(({ time }) => {
        const change = (Math.random() * 2 - 1) * baseValue * 0.05;
        currentValue = Math.max(baseValue * 0.5, Math.min(baseValue * 1.5, currentValue + change));
        return {
            value: currentValue,
            time: moment.unix(time).toISOString(),
        };
    });

    cachedTVL[cacheKey] = data;
    return data;
};

const useMockHistoricalLockedPoolLiquidity = (
    liquidityPoolId: string,
    timeframe: ValueOf<typeof config.areaChartTimeframes>
) => {
    const baseValue = tvlStartValues[liquidityPoolId] ?? 1_000_000;

    const defaultData = useMemo(() => {
        return generateMockTVLData(timeframe, liquidityPoolId, baseValue);
    }, [timeframe, liquidityPoolId, baseValue]);

    const formatData = useCallback(
        (baseTVL: number, tf: ValueOf<typeof config.areaChartTimeframes>, poolId: string) => {
            return generateMockTVLData(tf, poolId, baseTVL);
        },
        []
    );

    const formattedData = useMemo(() => {
        return formatData(baseValue, timeframe, liquidityPoolId);
    }, [baseValue, timeframe, liquidityPoolId, formatData]);

    return {
        defaultData,
        historicalLockedPoolLiquidity: formattedData,
        isLoading: false,
        error: undefined,
        mutate: () => {},
    };
};

export default useMockHistoricalLockedPoolLiquidity;
